import { useState } from 'react'
import * as Popover from '@radix-ui/react-popover'
import type { Assignee } from '@fitting-room/contracts'
import { ChevronDown } from 'lucide-react'
import { StaffMark } from './StaffMark'

/** The filter's value for "nobody yet" — the API's `assignee=none`. */
export const UNASSIGNED = 'none'

/**
 * The appointment list's assignee filter, ported from tperent's
 * `AssigneeFilterPopover`.
 *
 * It shares one set with the "Mine" and "Unassigned" chips beside it: this is
 * the whole team laid out to pick from, not a third filter, so ticking
 * yourself here lights "Mine" too — both are saying the same thing.
 *
 * A popover rather than a row of chips because a team can be a dozen people,
 * and spread across the filter bar they would push the appointments — the
 * point of the screen — down below the fold on a day nobody touches it.
 */
export function AssigneeFilterPopover({
  members,
  selected,
  onChange,
}: {
  readonly members: readonly Assignee[]
  readonly selected: readonly string[]
  readonly onChange: (next: string[]) => void
}) {
  const [open, setOpen] = useState(false)
  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id])

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        {/* Pressed while anything is picked: collapsed, the count is the only
            sign the list is filtered at all, and a filter that is on but
            looks off is the one way this control can mislead. */}
        <button type="button" className="st-chip" aria-pressed={selected.length > 0}>
          Bridal stylist
          {selected.length > 0 ? <span className="tabular-nums opacity-70">{selected.length}</span> : null}
          <ChevronDown size={12} aria-hidden />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content className="st-popover w-60" align="start" sideOffset={6}>
          <div className="mb-1 flex items-center justify-between border-b pb-1.5" style={{ borderColor: 'var(--st-rule)' }}>
            <span className="st-eyebrow">Bridal stylist</span>
            <button type="button" className="st-meta underline" onClick={() => onChange([])} disabled={selected.length === 0}>
              Clear
            </button>
          </div>
          {/* Unassigned first: the appointments nobody has claimed are the ones
              that most need finding. */}
          <FilterRow label="Unassigned" checked={selected.includes(UNASSIGNED)} onToggle={() => toggle(UNASSIGNED)} />
          {members.map((member) => (
            <FilterRow
              key={member.id}
              label={member.active ? member.displayName : `${member.displayName} (inactive)`}
              mark={member.calendarMark}
              checked={selected.includes(member.id)}
              onToggle={() => toggle(member.id)}
            />
          ))}
          {members.length === 0 ? <p className="st-meta px-1 py-2 text-xs">Nobody has been added to the team yet.</p> : null}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

function FilterRow({
  label,
  mark,
  checked,
  onToggle,
}: {
  readonly label: string
  readonly mark?: string | undefined
  readonly checked: boolean
  readonly onToggle: () => void
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 px-1 py-1.5 text-sm">
      <input type="checkbox" checked={checked} onChange={onToggle} />
      <StaffMark mark={mark} name={label} />
      <span className="truncate">{label}</span>
    </label>
  )
}
